import {
  FaBookOpen,
  FaFolderOpen,
} from "react-icons/fa";

export const CMS_PRIMARY = "#007BFF";

export const SOP_PAGE_SHELL = "px-2 pb-8 sm:px-3";

export const SOP_TITLE_SHELL = "tailwind-container px-2 pt-4 sm:px-3 sm:pt-5";

export { MODULE_PAGE_SHELL_STYLE } from "../TaskManagement/workspaceConstants";

export const SOP_POLICY_STAT_CARDS = [
  {
    key: "total",
    label: "Total Policies",
    sub: "Published documents",
    Icon: FaBookOpen,
    color: CMS_PRIMARY,
    iconBg: "rgba(0, 123, 255, 0.1)",
  },
  {
    key: "categories",
    label: "Categories",
    sub: "Unique groupings",
    Icon: FaFolderOpen,
    color: "#6366f1",
    iconBg: "rgba(99, 102, 241, 0.1)",
  },
];

export const formatPolicyDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const getDaysUntilReview = (reviewDate) => {
  if (!reviewDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const review = new Date(reviewDate);
  review.setHours(0, 0, 0, 0);
  return Math.ceil((review - today) / (1000 * 60 * 60 * 24));
};

export const isReviewDueSoon = (reviewDate, withinDays = 30) => {
  const days = getDaysUntilReview(reviewDate);
  if (days === null) return false;
  return days >= 0 && days <= withinDays;
};

export const truncateText = (text = "", max = 140) => {
  const trimmed = String(text).trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max).trim()}…`;
};

export const normalizeToken = (value = "") => String(value).toLowerCase().trim();

export const isTeamLeadEmployee = (employee = {}) => {
  if (employee.is_team_lead === 1 || employee.is_team_lead === true) return true;

  const roleHaystack = [employee.role, employee.user_role, employee.department]
    .map(normalizeToken)
    .filter(Boolean)
    .join(" ");

  return roleHaystack.includes("team lead") || roleHaystack.includes("teamlead");
};

const POLICY_AUDIENCE_RULES = [
  {
    key: "team_lead",
    label: "Team Lead",
    matchesPolicy: (policy) => {
      const name = normalizeToken(policy?.name);
      return name.includes("team lead") || name.includes("teamlead");
    },
    matchesEmployee: isTeamLeadEmployee,
  },
];

export const getPolicyAudience = (policy) => {
  const rule = POLICY_AUDIENCE_RULES.find((item) => item.matchesPolicy(policy));
  if (rule) {
    return { key: rule.key, label: rule.label, isGeneral: false };
  }

  return { key: "general", label: "All staff", isGeneral: true };
};

export const policyAppliesToEmployee = (policy, employee) => {
  const audience = getPolicyAudience(policy);
  if (audience.isGeneral) return true;

  const rule = POLICY_AUDIENCE_RULES.find((item) => item.key === audience.key);
  return rule ? rule.matchesEmployee(employee) : true;
};

export const getPoliciesForEmployee = (policies = [], employee) =>
  policies.filter((policy) => policyAppliesToEmployee(policy, employee));

export const filterPoliciesBySearch = (policies = [], searchTerm = "") => {
  const query = normalizeToken(searchTerm);
  if (!query) return policies;

  return policies.filter((policy) => {
    const haystack = [
      policy.name,
      policy.description,
      policy.category?.name,
      getPolicyAudience(policy).label,
    ]
      .filter(Boolean)
      .map(normalizeToken)
      .join(" ");

    return haystack.includes(query);
  });
};

export const buildPersonalizedPolicyRows = (policies = [], employees = [], searchTerm = "") => {
  return employees.flatMap((employee) =>
    filterPoliciesBySearch(getPoliciesForEmployee(policies, employee), searchTerm).map(
      (policy) => ({
        employee,
        policy,
        audience: getPolicyAudience(policy),
      }),
    ),
  );
};

export const groupEmployeesByBranch = (groupedBranches = [], assignedUserIds = new Set()) =>
  groupedBranches
    .map((group) => ({
      branch_id: group.branch_id,
      branch_name: group.branch_name || "Unassigned",
      users: (group.users || []).filter((user) => !assignedUserIds.has(String(user.id))),
    }))
    .filter((group) => group.users.length);

export const groupEmployeesByDepartment = (employees = []) => {
  const grouped = employees.reduce((acc, user) => {
    const department = String(user.department || "").trim() || "Unassigned";
    if (!acc[department]) acc[department] = [];
    acc[department].push(user);
    return acc;
  }, {});

  return Object.entries(grouped)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([department, users]) => ({
      department,
      users: [...users].sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" }),
      ),
    }));
};
